import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import PageWrapper from "@components/layouts/PageWrapper";
import Search from "@components/layouts/Search";
import { useLocation, useNavigate } from "react-router-dom";
import usePagination from "@hooks/usePagination";
import useSearch from "@hooks/useSearch";
import { useDeleteProducutTagMutation } from "@states/product/product-tag.api";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { castTypeArr } from "@utils/general/cast-type";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { DeletePopup } from "@helpers/popups/DeletePopup";
import TableSkeleton from "@components/layouts/TableSkeleton";
import { TDeleteSuccesResponse } from "@type-defs/general/TDeleteSuccessResponse";
import { Toast } from "@helpers/popups/Toast";
import useGetAllStockImports from "@hooks/warehouse-and-inventory/useGetAllStockImports";
import { STOCK_IMPORT } from "@utils/constants/warehouse-and-inventory/stock-import.constants";
import { stockImportColumns } from "@helpers/columns/warehouse-and-inventory/stock-import.columns";
import useGetAllStockAdjustments from "@hooks/warehouse-and-inventory/useGetAllStockAdjustment";
import { useDeleteStockAdjustmentMutation } from "@states/warehouse-and-inventory/stock-adjustment.api";
import { STOCK_ADJUSTMENT } from "@utils/constants/warehouse-and-inventory/stock-adjustment.constants";
import { stockAdjustmentColumns } from "@helpers/columns/warehouse-and-inventory/stock-adjustment..report.columns";
import NoRecord from "@components/layouts/NoRecord";
import InfoModal from "@components/modals/common/InfoModal";
import StockAdjustmentDetail from "@components/extra/warehouse-and-inventory/StockAdjustmentDetai";
import { TStockAdjustment } from "@helpers/validation-schemas/warehouse-and-inventory/stock-adjustment.schema";
import AuthComponent from "@components/auth/component";
import useGetPermission from "@hooks/common/useGetPermission";
import ReportFilter from "@components/filters/ReportFilter";
import { formatFilterDate } from "@utils/general/format-filter-date";
import CustomButton from "@components/buttons/CustomButton";
import * as XLSX from "xlsx";

const StockAdjustmentReportPage = () => {
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(today.getMonth() - 1);
  const filterRef = useRef({
    from_date: undefined,
    to_date: undefined,
  });

  const { hasAccess } = useGetPermission();
  const { page, limit, handlePageChange, handlePerRowsChange } =
    usePagination();
  const { search, setSearch } = useSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [stockAdjustmentDetail, setStockAdjustmentDetail] = useState<
    TStockAdjustment | undefined
  >(undefined);

  const {
    stockAdjustmentError,
    stockAdjustmentRefetch,
    stockAdjustmentFetching,
    stockAdjustmentPaginator,
    stockAdjustmentData,
  } = useGetAllStockAdjustments({
    page,
    limit,
    search,
    //@ts-ignore
    from_date: filterRef.current?.from_date || formatFilterDate(lastMonth),
    //@ts-ignore
    to_date: filterRef.current?.to_date || formatFilterDate(today),
  });

  const [deleteStockAdjustment, { error: deleteError, isLoading }] =
    useDeleteStockAdjustmentMutation();

  // a custom hook that will handle fetch/mutation errors
  useErrorHandling(
    ...castTypeArr<TFailResponse>([stockAdjustmentError, deleteError])
  );

  useEffect(() => {
    if (location.state?.reload) {
      stockAdjustmentRefetch();
    }
  }, [location.state?.reload]);

  const toggleModalHandler = (adjustment: TStockAdjustment) => {
    setStockAdjustmentDetail(adjustment);
    setShowInfoModal(true);
  };

  const editHandler = (warehouse_id: string | number) => {
    navigate(`${STOCK_IMPORT.BASE_PATH}${warehouse_id}/edit`);
  };

  const deleteHandler = async (id: number | string) => {
    try {
      const { isConfirmed } = await DeletePopup.fire();

      if (isConfirmed) {
        const res = await deleteStockAdjustment(id);

        const successData = "data" in res ? res.data : null;
        const { success, message } = successData as TDeleteSuccesResponse;

        if (success) {
          Toast.fire({
            icon: "success",
            title: message,
          });

          stockAdjustmentRefetch();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };
  const handleExportClick = () => {
    if (!stockAdjustmentData || stockAdjustmentData.length === 0) {
      alert("No data to export");
      return;
    }

    // Prepare and flatten data
    const exportData = stockAdjustmentData.map((item: any) => ({
      ID: item.inventory_adjustment_id,
      Date: new Date(item.inventory_adjustment_date).toLocaleDateString(),
      Product: item.product_name,
      Code: item.product_code,
      Quantity: item.qty,
      Unit: item.unit_name,
      Method: item.adjustment_method,
      Description: item.description || "",
      Warehouse: item.warehouse_name,
      CreatedBy: item.created_name || "",
      CreatedTime: new Date(item.created_time).toLocaleString(),
      Status: item.status === 1 ? "Active" : "Inactive",
    }));

    // Convert to worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Create a workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Adjustments");

    // Export the workbook as an Excel file
    XLSX.writeFile(workbook, "stock-adjustment-report.xlsx");
  };

  return (
    <AuthComponent
      action="view"
      module="Stock & Inventory"
      subModule="Stock Adjustment"
      page
    >
      <React.Fragment>
        {/* <Search setSearch={setSearch} /> */}
        <PageWrapper title={`Stock Adjustment Report`}>
          <div className="grid grid-cols-8 justify-evenly">
            <div className="col-span-6">
              {/* @ts-ignore */}
              <ReportFilter
                refetch={stockAdjustmentRefetch}
                filterRef={filterRef}
              />
            </div>
            <div className=" flex items-center justify-end  mt-2 col-span-2">
              <CustomButton
                title={
                  <div className="flex  gap-3">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.4126 17.2325V1H10.102C6.73567 1.60819 3.37848 2.17076 0 2.75462V18.1083C3.32374 18.6952 6.69006 19.2821 10.0351 19.8903H11.4035V17.2325H11.4126ZM6.46198 13.8632C6.04269 12.9666 5.67515 12.0467 5.36117 11.1081C5.05707 11.9991 4.63742 12.8384 4.2938 13.7111C3.81333 13.7111 3.33286 13.6837 2.8524 13.6655C3.41497 12.5616 3.9593 11.4486 4.54011 10.3539C4.04748 9.22268 3.50316 8.11579 2.99532 6.99368L4.44889 6.91158C4.77427 7.76912 5.1331 8.61754 5.40374 9.49637C5.70783 8.5628 6.12444 7.67181 6.49544 6.78386C6.99212 6.74939 7.48982 6.71898 7.98853 6.69263C7.40467 7.89278 6.81575 9.09598 6.22175 10.3022C6.82994 11.5186 7.43813 12.7502 8.04631 13.9787C7.51719 13.9361 6.99111 13.9027 6.46198 13.8632Z"
                        fill="white"
                      />
                      <path
                        d="M11.9658 3.10352V4.26819H14.2921V5.98632H11.9658V6.92901H14.2921V8.64714H11.9658V9.58983H14.2921V11.3049H11.9658V12.2476H14.2921V13.9505H11.9658V14.948H14.2921V16.6113H11.9658V17.776H20V3.10352H11.9658ZM18.2271 16.6235H14.8456V14.9601H18.2271V16.6235ZM18.2271 13.9627H14.8456V12.2446H18.2271V13.9627ZM18.2271 11.3019H14.8456V9.58679H18.2271V11.3019ZM18.2271 8.64106H14.8456V6.92597H18.2271V8.64106ZM18.2271 5.98024H14.8456V4.26819H18.2271V5.98024Z"
                        fill="white"
                      />
                    </svg>
                    Export Excel
                  </div>
                }
                color="bg-[#1E6E42] h-3/6"
                handleClick={handleExportClick}
                className=""
              />
            </div>
          </div>
          <InfoModal
            title="Stock Adjustment Detail"
            isOpen={showInfoModal}
            setIsOpen={setShowInfoModal}
          >
            {/* @ts-ignore */}
            <StockAdjustmentDetail adjustment={stockAdjustmentDetail} />
          </InfoModal>
          <DataTable
            keyField="product_inventory_id"
            responsive
            striped
            pagination
            paginationServer
            //@ts-ignore
            paginationTotalRows={stockAdjustmentPaginator?.totalItems}
            progressPending={stockAdjustmentFetching}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoRecord />}
            progressComponent={<TableSkeleton row={10} actionRight col={9} />}
            //@ts-ignore
            columns={stockAdjustmentColumns(
              editHandler,
              deleteHandler,
              toggleModalHandler
            )}
            //@ts-ignore
            data={stockAdjustmentData}
          />
        </PageWrapper>
      </React.Fragment>
    </AuthComponent>
  );
};

export default StockAdjustmentReportPage;
